import "../../App.css";
import { LessonWidget } from "../../components/LessonWidget";
import lighting from "../../img/lighting.svg";
import application from "../../img/mainColor.svg";
import profile_icon from "../../img/profile-icon.svg";
import bell_icon from "../../img/bell-icon.svg";
import { Link } from "react-router-dom";
import {
  ILesson,
  ISection,
  IStory,
  IUser,
  IUserScheduled,
  TIME_HOURS_LS,
  TIME_MINUTE_LS,
} from "../../type";
import { HabitDayPadItem } from "../../components/HabitDayPadItem";
import { shouldShowLessonsOfflineBanner } from "../../utils/lessonsOfflineBanner";
import {
  allLessonsCompletedInList,
  calculateComplitedLesson,
  getActiveScheduleForSection,
  getCurrentScheduled,
  getCurrentSection,
  getLatestStartedSchedule,
  getNextSectionBySequence,
  habitLessonDayIndex,
  hasGold,
  hasPearl,
  isCompletedLesson,
  buildHabitDayPadTracks,
  prewiusScheduled,
  resolveLessonsForScheduledRound,
} from "../../utils/current_section";
import { Fragment, useEffect, useRef, useState } from "react";
import { getNotifications, markAllNotificationsRead, markNotificationRead, TAppNotification } from "../../api/notifications";
import { logClientDiagnostic } from "../../utils/clientDiagnosticLog";
import "./index.css";
import { StoryLineItem } from "../../components/StoryLineItem";

const NOTIFICATIONS_ENABLED = false;

type HabitStripCtx = {
  lessons: ILesson[];
  scheduled: IUserScheduled;
};

function renderHabitLessonItems(ctx: HabitStripCtx) {
  const { lessons, scheduled } = ctx;
  return lessons.map((p) => {
    const index = habitLessonDayIndex(p, lessons);
    let st: "done" | "pass" | "access" | "off" = "off";

    const today = new Date();
    const targetDate = new Date(scheduled.start_date);
    const h = today.getTimezoneOffset() / 60;
    targetDate.setUTCHours(TIME_HOURS_LS + h, TIME_MINUTE_LS, 0, 0);
    const daysDifference = Math.floor(
      Math.abs((today.getTime() - targetDate.getTime()) / (1000 * 3600 * 24))
    );

    if (isCompletedLesson(p.Exercise_LessonRelation)) st = "done";
    else if (daysDifference < index) {
      st = "off";
      return (
        <HabitDayPadItem
          title={"День " + p.sequence_number__lesson}
          status={st}
          key={p.id}
        />
      );
    } else if (daysDifference === index) {
      st = "access";
    } else {
      st = "pass";
      return (
        <Link to={"/section/" + p.section_id + "/lesson/" + p.id} key={p.id}>
          <HabitDayPadItem title={"День " + p.sequence_number__lesson} status={st} />
        </Link>
      );
    }

    return (
      <Link to={"/section/" + p.section_id + "/lesson/" + p.id} key={p.id}>
        <HabitDayPadItem title={"День " + p.sequence_number__lesson} status={st} />
      </Link>
    );
  });
}

interface IProps {
  user: IUser;
  sections: ISection[];
  sectionsLoadedOnce: boolean;
  lessonsRefreshFailed?: boolean;
  stories: IStory[];
  reload_lessons: () => void;
  coinSilver: number;
}

export function Main(props: IProps) {
  const pageRef = useRef<HTMLElement | null>(null);
  const dayPadRef = useRef<HTMLDivElement | null>(null);
  const suppressNextClickRef = useRef<boolean>(false);
  const [notifications, setNotifications] = useState<TAppNotification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeNotification, setActiveNotification] = useState<TAppNotification | null>(null);
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const reloadNotifications = () => {
    getNotifications().then((r) => setNotifications(r.items));
  };

  useEffect(() => {
    if (!NOTIFICATIONS_ENABLED) return;
    reloadNotifications();
    const t = setInterval(reloadNotifications, 20000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const pageEl = pageRef.current;
    const dayPadEl = dayPadRef.current;
    if (!pageEl) return;

    const dragState = {
      active: false,
      mode: "vertical" as "vertical" | "horizontal",
      startX: 0,
      startY: 0,
      startScrollTop: 0,
      startScrollLeft: 0,
      moved: false,
    };

    const getScrollablePage = () => {
      // For this layout, outer section has overflow-y, but fallback to window scrolling.
      return pageEl;
    };

    const isInteractive = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      if (!el) return false;
      return !!el.closest("input, textarea, select, button, a, [role='button'], [contenteditable='true']");
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      if (isInteractive(e.target)) return;

      const target = e.target as HTMLElement | null;
      const inDayPad = !!(target && dayPadEl && dayPadEl.contains(target));

      dragState.active = true;
      dragState.mode = inDayPad ? "horizontal" : "vertical";
      dragState.startX = e.clientX;
      dragState.startY = e.clientY;
      dragState.startScrollTop = getScrollablePage().scrollTop;
      dragState.startScrollLeft = dayPadEl ? dayPadEl.scrollLeft : 0;
      dragState.moved = false;

      pageEl.style.cursor = "grabbing";
      if (dayPadEl) dayPadEl.style.cursor = "grabbing";
      e.preventDefault();
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!dragState.active) return;

      const dx = e.clientX - dragState.startX;
      const dy = e.clientY - dragState.startY;
      if (Math.abs(dx) > 6 || Math.abs(dy) > 6) {
        dragState.moved = true;
      }

      if (dragState.mode === "horizontal" && dayPadEl) {
        dayPadEl.scrollLeft = dragState.startScrollLeft - dx;
      } else {
        const scrollable = getScrollablePage();
        scrollable.scrollTop = dragState.startScrollTop - dy;
      }
      e.preventDefault();
    };

    const stopDrag = () => {
      if (!dragState.active) return;
      if (dragState.moved) {
        suppressNextClickRef.current = true;
      }
      dragState.active = false;
      pageEl.style.cursor = "";
      if (dayPadEl) dayPadEl.style.cursor = "";
    };

    pageEl.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", stopDrag);
    pageEl.addEventListener("mouseleave", stopDrag);

    if (dayPadEl) {
      dayPadEl.style.cursor = "grab";
      dayPadEl.style.userSelect = "none";
    }

    return () => {
      pageEl.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", stopDrag);
      pageEl.removeEventListener("mouseleave", stopDrag);
    };
  }, []);

  const onPageClickCapture = (e: React.MouseEvent) => {
    if (!suppressNextClickRef.current) return;
    suppressNextClickRef.current = false;
    e.preventDefault();
    e.stopPropagation();
  };

  if (props.sections.length === 0 && !props.sectionsLoadedOnce) {
    return (
      <div className="mainPage-lessonsLoading" role="status" aria-live="polite">
        <div className="mainPage-lessonsLoading-spinner" aria-hidden />
        <p className="mainPage-lessonsLoading-text">Загрузка уроков...</p>
      </div>
    );
  }
  if (props.sections.length === 0 && props.sectionsLoadedOnce) {
    return (
      <div className="mainPage-lessonsLoading" role="status" aria-live="polite">
        <p className="mainPage-lessonsLoading-text">
          Не удалось быстро загрузить уроки. Нажмите, чтобы попробовать снова.
        </p>
        <button className="btn btn-fill" onClick={props.reload_lessons}>
          Повторить загрузку
        </button>
      </div>
    );
  }
  let currentScheduled: IUserScheduled =
    getLatestStartedSchedule(props.sections) ?? getCurrentScheduled(props.sections);
  let currentSection: ISection = getCurrentSection(props.sections, currentScheduled);
  /** Для списка "Уроки мудрости" используем единый круг по всей ленте (22 качества). */
  const wisdomRound = Number(currentScheduled?.round ?? 0);
  /** Активный круг именно для текущего модуля (не глобальный round из «чужого» слота). */
  let activeScheduleForCurrent =
    getActiveScheduleForSection(currentSection) ?? currentScheduled;
  const scheduledRound = Number(
    activeScheduleForCurrent?.round ?? currentScheduled?.round ?? 0
  );
  let resolvedCurrent = resolveLessonsForScheduledRound(currentSection, scheduledRound);
  let currentLessons = resolvedCurrent.lessons;
  let effectiveRound = resolvedCurrent.effectiveRound;

  if (resolvedCurrent.usedFallback) {
    logClientDiagnostic({
      action: "habit.round_fallback_used",
      description: "Habit tracker round fallback used",
      endpoint: "/miniapi/get_current_lessons",
      method: "POST",
      userId: props.user?.id || null,
      additionalData: {
        sectionId: currentSection?.id || null,
        scheduledRound,
        fallbackRound: effectiveRound,
        lessonsInFallbackRound: currentLessons.length,
      },
    });
  }

  // Модуль пройден, а фокус ещё на прошлой секции — показываем следующий модуль с расписанием.
  if (
    allLessonsCompletedInList(currentLessons) &&
    currentLessons.length > 0
  ) {
    const nextSection = getNextSectionBySequence(props.sections, currentSection);
    const nextSched = nextSection
      ? getActiveScheduleForSection(nextSection) ??
        nextSection.UserLessonsScheduled?.find(
          (u) => Number(u.round) === Number(scheduledRound)
        )
      : undefined;
    if (nextSection && nextSched) {
      const resolvedNext = resolveLessonsForScheduledRound(
        nextSection,
        Number(nextSched.round)
      );
      if (resolvedNext.lessons.length > 0) {
        const fromSectionId = currentSection.id;
        currentSection = nextSection;
        activeScheduleForCurrent = nextSched;
        currentScheduled = nextSched;
        resolvedCurrent = resolvedNext;
        currentLessons = resolvedNext.lessons;
        effectiveRound = resolvedNext.effectiveRound;
        logClientDiagnostic({
          action: "habit.advanced_to_next_section",
          description: "Habit strip advanced after completing current module",
          endpoint: "/miniapi/get_current_lessons",
          method: "POST",
          userId: props.user?.id || null,
          additionalData: {
            fromSectionId,
            toSectionId: nextSection.id,
            toSectionSeq: nextSection.sequence_number__section,
            lessonsCount: currentLessons.length,
            firstDay: currentLessons[0]?.sequence_number__lesson,
            lastDay: currentLessons[currentLessons.length - 1]?.sequence_number__lesson,
          },
        });
      }
    }
  }

  const c = prewiusScheduled(props.sections);
  let prewSection: ISection | undefined;
  if (c) {
    prewSection = props.sections.find((s) => s.id === c.SectionId) as ISection;
    const isSameSectionAsCurrent = Number(prewSection?.id) === Number(currentSection?.id);
    if (isSameSectionAsCurrent && prewSection) {
      const previousSectionLessons = prewSection.Lesson_SectionRelation.filter(
        (l) => l.round === c.round
      );
      // Не смешиваем разные модули в day-pad:
      // подтягиваем только хвост незавершенного предыдущего круга
      // в рамках текущего раздела (same section).
      const unfinishedPreviousLessons = previousSectionLessons.filter(
        (lesson) => !isCompletedLesson(lesson.Exercise_LessonRelation)
      );
      if (unfinishedPreviousLessons.length > 0) {
        const currentSequenceSet = new Set(
          currentLessons.map((lesson) => Number(lesson.sequence_number__lesson || 0))
        );
        // Убираем дубли day-pad по номеру дня (sequence):
        // если день уже есть в текущем круге, не подтягиваем его из предыдущего.
        const uniquePreviousTail = unfinishedPreviousLessons.filter(
          (lesson) => !currentSequenceSet.has(Number(lesson.sequence_number__lesson || 0))
        );
        currentLessons = [...uniquePreviousTail, ...currentLessons];
      }
    }
  }
  // Нормализуем порядок для day pad:
  // API может вернуть смесь "предыдущая+текущая ступень" в несортированном виде,
  // из-за чего визуально получается 139,140,134...
  // Сначала дедуп по id, затем строгая сортировка по sequence_number__lesson.
  currentLessons = Array.from(
    new Map(currentLessons.map((lesson) => [lesson.id, lesson])).values()
  ).sort(
    (a, b) =>
      Number(a.sequence_number__lesson || 0) -
      Number(b.sequence_number__lesson || 0)
  );

  const habitDayPadTracks = buildHabitDayPadTracks(
    props.sections,
    currentSection,
    wisdomRound,
    currentLessons,
    currentScheduled
  );
  /** В "Уроки мудрости" показываем ровно 22 качества, без дублей секций. */
  const wisdomSections = Array.from(
    new Map(
      [...(props.sections || [])]
        .sort((a, b) => {
          const sa = Number(a.sequence_number__section || 0);
          const sb = Number(b.sequence_number__section || 0);
          if (sa === sb) return Number(a.id || 0) - Number(b.id || 0);
          return sa - sb;
        })
        .map((section) => [Number(section.sequence_number__section || 0), section])
    ).values()
  ).slice(0, 22);

  let src: string;
  if (props.user.avatarLink !== "no-avatar") {
    src = props.user.avatarLink;
  } else {
    src = "./avatar.png";
  }

  return (
    <section
      className="section mainPage mainPage-safeTop flex flex-column"
      ref={pageRef}
      onClickCapture={onPageClickCapture}
    >
      {shouldShowLessonsOfflineBanner(
        props.sections.length,
        !!props.lessonsRefreshFailed
      ) && (
        <div className="lessons-offline-banner" role="status" aria-live="polite">
          <p className="lessons-offline-banner__text">
            Нет связи — показаны сохранённые уроки. Обновление не удалось.
          </p>
          <button
            type="button"
            className="lessons-offline-banner__btn"
            onClick={props.reload_lessons}
          >
            Повторить
          </button>
        </div>
      )}
      {/* Wiget */}
      <div className="mainPage_wiget flex flex-row">
        <img
          src={src}
          className="mainPage_wiget_icon__img"
          alt="аватарка на главной странице"
        />
        <div className="mainPage_wiget_textContent">
          <div className="mainPage_wiget_textContent__greeting flex flex-row">
            Привет, {props.user.name}!
          </div>
          <div className="mainPage_wiget_textContent__description">
            Самое время начать учиться
          </div>
        </div>
        {NOTIFICATIONS_ENABLED && (
          <button
            type="button"
            className="mainPage_notifyBell"
            onClick={() => setShowNotifications((p) => !p)}
            aria-label="Уведомления"
          >
            <img src={bell_icon} alt="" className="mainPage_notifyBellIcon" />
            {unreadCount > 0 && <span className="mainPage_notifyBadge">{unreadCount}</span>}
          </button>
        )}
      </div>
      {NOTIFICATIONS_ENABLED && showNotifications && (
        <div className="mainPage_notifyDropdown">
          <div className="mainPage_notifyHeader flex flex-row">
            <span>Уведомления</span>
            <button
              type="button"
              className="mainPage_notifyReadAll"
              onClick={() => {
                markAllNotificationsRead().then(reloadNotifications);
              }}
            >
              Прочитать все
            </button>
          </div>
          <div className="mainPage_notifyList">
            {notifications.length === 0 && (
              <div className="mainPage_notifyEmpty">Пока уведомлений нет</div>
            )}
            {notifications.map((n) => (
              <button
                key={n.id}
                type="button"
                className={`mainPage_notifyItem ${n.isRead ? "" : "mainPage_notifyItem-unread"}`}
                onClick={() => {
                  setActiveNotification(n);
                  if (!n.isRead) {
                    markNotificationRead(n.id).then(reloadNotifications);
                  }
                }}
              >
                {n.imageUrl ? (
                  <img src={n.imageUrl} className="mainPage_notifyPreview" alt="" />
                ) : (
                  <span className="mainPage_notifyPreview mainPage_notifyPreview-empty">•</span>
                )}
                <span className="mainPage_notifyTitle">{n.title}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Story Line */}
      <div className="mainPage_storyLine-box">
        <div className="mainPage_storyLine flex flex-row">
          {(Array.isArray(props.stories) ? props.stories : []).map((story, index) => {
            return (
              <Link to={"/story/" + story.id} key={index}>
                <StoryLineItem
                  title={story.title}
                  url={story.url}
                  border={story.StorysViewedRelation.length === 0}
                />
              </Link>
            );
          })}
        </div>
      </div>
      {/* Habit trecker */}
      <div className="mainPage_habit">
        <div className="mainPage_habit_header">Формирование привычки</div>
        <div className="mainPage_habit_dayPad" ref={dayPadRef}>
          {habitDayPadTracks.length === 0 ? (
            <div className="mainPage-habitEmpty">
              Нет доступных уроков для текущего круга. Обновите страницу чуть позже.
            </div>
          ) : (
            <>
              {habitDayPadTracks.map((track, trackIndex) => (
                <Fragment key={track.sectionId}>
                  {trackIndex > 0 && (
                    <div className="mainPage_habit_dayPad-moduleDivider" aria-hidden />
                  )}
                  <div
                    className="mainPage_habit_dayPad-moduleTrack"
                    data-module={
                      trackIndex === habitDayPadTracks.length - 1 ? "current" : "previous"
                    }
                  >
                    {renderHabitLessonItems({
                      lessons: track.lessons,
                      scheduled: track.scheduled,
                    })}
                  </div>
                </Fragment>
              ))}
            </>
          )}
        </div>
      </div>
      {/* Lessons Wiget */}
      <div className="mainPage_lessonWisdom">
        <div className="mainPage_lessonWisdom_header">Уроки мудрости</div>
        <div className="mainPage_lessonWisdom_Pad">
          <div className="mainPage_lessonWisdom_Pad_leftColumn flex flex-column">
            {wisdomSections
              .map((section, ind) => {
                if (ind % 2 === 1) return false;
                const sectionRound = wisdomRound;
                /** В БД round уже 1-based (1,2,3); подпись совпадает с кругом. */
                const sectionRoundLabel = Number(sectionRound) || 1;
                let { complitedLesson, lockedSection } =
                  calculateComplitedLesson(section, sectionRound, props.sections);
                const gold = hasGold(section);
                const pearl = hasPearl(section);

                return (
                  <LessonWidget
                    index={section.sequence_number__section}
                    title={section.title}
                    step={"Ступень " + sectionRoundLabel}
                    totalLesson={7}
                    complitedLesson={complitedLesson}
                    lockedSection={lockedSection}
                    gold={gold}
                    pearl={pearl}
                    key={ind}
                    preivSection={prewSection?.id === section.id}
                  />
                );
              })
              .filter((w) => {
                return w !== false;
              })}
          </div>
          <div className="mainPage_lessonWisdom_Pad_rightColumn flex flex-column">
            {wisdomSections
              .map((section, ind) => {
                if (ind % 2 === 0) return false;
                const sectionRound = wisdomRound;
                /** В БД round уже 1-based (1,2,3); подпись совпадает с кругом. */
                const sectionRoundLabel = Number(sectionRound) || 1;
                let { complitedLesson, lockedSection } =
                  calculateComplitedLesson(section, sectionRound, props.sections);
                const gold = hasGold(section);
                const pearl = hasPearl(section);

                return (
                  <LessonWidget
                    index={section.sequence_number__section}
                    title={section.title}
                    step={"Ступень " + sectionRoundLabel}
                    totalLesson={7}
                    complitedLesson={complitedLesson}
                    lockedSection={lockedSection}
                    gold={gold}
                    pearl={pearl}
                    key={ind}
                    preivSection={prewSection?.id === section.id}
                  />
                );
              })
              .filter((w) => {
                return w !== false;
              })}
          </div>
        </div>
      </div>
      {/* Footer Menu */}
      <div className="profile_footer">
        <Link to="/rating">
          <div className="footer_tab">
            <img src={lighting} alt="Комьюнити" />
            <span className="textMenu">Комьюнити</span>
          </div>
        </Link>
        <Link to="/">
          <div className="footer_tab">
            <img src={application} alt="Главная" />
            <span className="textMenu">Главная</span>
          </div>
        </Link>
        <Link to="/profile">
          <div className="footer_tab">
            <img src={profile_icon} alt="Профиль" />
            <span className="textMenu">Профиль</span>
          </div>
        </Link>
      </div>
      {NOTIFICATIONS_ENABLED && activeNotification && (
        <section className="mainPage_notifyModal">
          <div className="mainPage_notifyModalCard">
            <h3>{activeNotification.title}</h3>
            {activeNotification.imageUrl && (
              <img src={activeNotification.imageUrl} alt="" className="mainPage_notifyModalImage" />
            )}
            <p>{activeNotification.body || "Без текста"}</p>
            <button
              type="button"
              className="mainPage_notifyModalClose"
              onClick={() => setActiveNotification(null)}
            >
              Закрыть
            </button>
          </div>
        </section>
      )}
    </section>
  );
}
